import { renderSVG } from 'uqr';
import {
  sedrApi,
  type SedrAuditEvent,
  type SedrFile,
  type SedrGateResponse,
  type SedrGuestRoomContext,
  type SedrPhasesList,
  type SedrRequirement,
  type SedrVerifyResponse,
} from '../api/sedr';
import { ApiError } from '../api/client';
import { downloadAndDecryptSedrEvidence, sedrEvidenceNeedsRoomKey } from '../crypto/sedrEvidenceFile';
import { fetchAndDecryptSedrGuestRoomKey, resolveSedrGuestWrapKey } from '../crypto/sedrGuestInvite';
import { resolveSedrGuestFragment } from '../crypto/sedrGuestFragment';
import { formatFileSize } from '../crypto/sharedFolderFile';
import { deriveProofKeys, signGateChallenge } from '../crypto/infoShareFragment';
import { decodeBase64Url, encodeBase64Url } from '../crypto/primitives';
import { upsertPendingGuestInviteSeed } from '../crypto/guestInviteStash';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { setCryptoBusy } from '../ui/cryptoBusy';
import { showFilePreview } from '../ui/filePreviewDialog';
import { setGuestShellName } from '../ui/guestShellName';
import { renderSedrEvidenceExplorer } from './sedr/sedrEvidenceExplorer';
import {
  renderSedrCompliancePanel,
  type SedrCompliancePanelController,
} from './sedr/sedrCompliancePanel';
import {
  renderSedrDashboard,
  type CompliancePhaseId,
} from './sedr/sedrDashboard';
import {
  isSedrLifecycleClosed,
  renderRoomStatusBanner,
  seedSedrCategoriesCache,
  type SedrCategoryFilter,
  type SedrEvidenceFilter,
  type SedrLifecycleRequestFilter,
  type SedrMetricKind,
  type SedrOwnerFilter,
  type SedrRenewalFilter,
  type SedrStatusFilter,
} from './sedr/sedrUi';
import {
  buildSedrAuditLookup,
  mountSedrAuditExpandableTable,
} from './sedr/sedrAuditDetail';
import { createHelpTip } from '../help/helpTips';
import { sedrTabHelpTopic } from '../help/defaultTopic';
import {
  applySidebarCollapsed,
  createActivePanelControl,
  effectiveSidebarCollapsed,
  readSidebarCollapsed,
  renderSedrRoomChromeActions,
  SEDR_MOBILE_LAYOUT_MQ,
  writeSidebarCollapsed,
  type SedrChromeTab,
} from './sedr/sedrRoomChrome';
import {
  startGuestAccessExpiryMonitor,
  type GuestAccessExpiryHandle,
} from '../app/guestAccessExpiry';
import { logoutGuest } from '../app/index';

type SedrGuestTab = 'dashboard' | 'compliance' | 'evidence' | 'audit';

function parseGuestRoute(): { roomToken: string; guestInviteId: string } | null {
  const match = window.location.pathname.match(/^\/s\/([^/]+)\/g\/([^/]+)$/);
  return match ? { roomToken: match[1], guestInviteId: match[2] } : null;
}

export function GuestSedrPage(): HTMLElement {
  const page = document.createElement('section');
  page.className = 'module-page module-page--sedr';

  const route = parseGuestRoute();
  if (!route) {
    page.innerHTML = `<p class="error-text">${t('sedr.guest.invalidLink')}</p>`;
    return page;
  }

  let verified = false;
  let ctx: SedrGuestRoomContext | null = null;
  let roomKey: Uint8Array | null = null;
  let guestFragment = resolveSedrGuestFragment(route.roomToken);
  let activeTab: SedrGuestTab = 'dashboard';
  let complianceStatusFilter: SedrStatusFilter = [];
  let complianceCategoryFilter: SedrCategoryFilter = 'all';
  let compliancePhaseId: CompliancePhaseId = null;
  let complianceSearch = '';
  let complianceCommentSearch = '';
  let complianceHideObsolete = true;
  let complianceObsoleteSubtypes: SedrStatusFilter | undefined;
  let complianceOwnerFilter: SedrOwnerFilter = 'all';
  let complianceRenewalFilter: SedrRenewalFilter = 'all';
  let complianceLifecycleRequestFilter: SedrLifecycleRequestFilter = 'all';
  let complianceEvidenceFilter: SedrEvidenceFilter = 'all';
  let selectedRequirementId: string | null = null;
  let selectedRequirementIds: string[] = [];
  let requirements: SedrRequirement[] = [];
  let phasesList: SedrPhasesList = { phases: [], unassignedRequirementCount: 0 };
  let files: SedrFile[] = [];
  let auditEvents: SedrAuditEvent[] = [];
  let evidenceBusy = false;
  let evidenceParentId: string | null = null;
  let evidenceSearch = '';
  let lastGuestPassword: string | undefined;
  let compliancePanel: SedrCompliancePanelController | null = null;
  let sidebarPreference = readSidebarCollapsed();
  let chromeActionsCleanup: (() => void) | null = null;
  const sidebarMq = window.matchMedia(SEDR_MOBILE_LAYOUT_MQ);
  const isSidebarCollapsed = () => effectiveSidebarCollapsed(sidebarPreference);

  page.innerHTML = `
    <div class="module-section" id="sedr-guest-gate">
      <h2 data-i18n="sedr.guest.title"></h2>
      <p class="muted" data-i18n="sedr.guest.subtitle"></p>
      <form id="sedr-guest-form" class="form-grid" method="post" action="#" hidden>
        <label id="sedr-guest-password-wrap" hidden>
          <span data-i18n="sedr.guest.password"></span>
          <input type="password" name="password" autocomplete="current-password" />
        </label>
        <button type="submit" class="button primary" data-i18n="sedr.guest.enter"></button>
      </form>
      <div id="sedr-guest-totp-enroll" class="form-grid" hidden>
        <p class="muted" data-i18n="sedr.guest.totpEnrollHint"></p>
        <div id="sedr-guest-totp-qr" class="sedr-guest-totp-qr" aria-hidden="true"></div>
        <label>
          <span data-i18n="sedr.guest.totpSecret"></span>
          <input type="text" id="sedr-guest-totp-secret" readonly autocomplete="off" />
        </label>
        <form id="sedr-guest-totp-enroll-form" class="form-grid">
          <label>
            <span data-i18n="sedr.guest.totpCode"></span>
            <input type="text" name="totpCode" inputmode="numeric" autocomplete="one-time-code" maxlength="8" required />
          </label>
          <button type="submit" class="button primary" data-i18n="sedr.guest.totpConfirm"></button>
        </form>
      </div>
      <form id="sedr-guest-totp-challenge" class="form-grid" hidden>
        <p class="muted" data-i18n="sedr.guest.totpChallengeHint"></p>
        <label>
          <span data-i18n="sedr.guest.totpCode"></span>
          <input type="text" name="totpCode" inputmode="numeric" autocomplete="one-time-code" maxlength="8" required />
        </label>
        <button type="submit" class="button primary" data-i18n="sedr.guest.enter"></button>
      </form>
      <p class="error-text" id="sedr-guest-error" hidden></p>
      <p class="muted" id="sedr-guest-status" hidden></p>
    </div>
  `;
  applyI18n(page);
  page.querySelector('#sedr-guest-gate h2')?.append(createHelpTip('guest-sedr'));

  const gateSection = page.querySelector('#sedr-guest-gate') as HTMLElement;
  const form = page.querySelector('#sedr-guest-form') as HTMLFormElement;
  const enrollPanel = page.querySelector('#sedr-guest-totp-enroll') as HTMLElement;
  const enrollForm = page.querySelector('#sedr-guest-totp-enroll-form') as HTMLFormElement;
  const challengeForm = page.querySelector('#sedr-guest-totp-challenge') as HTMLFormElement;
  const qrHost = page.querySelector('#sedr-guest-totp-qr') as HTMLElement;
  const secretInput = page.querySelector('#sedr-guest-totp-secret') as HTMLInputElement;
  const errorEl = page.querySelector('#sedr-guest-error') as HTMLParagraphElement;
  const statusEl = page.querySelector('#sedr-guest-status') as HTMLParagraphElement;
  const passwordWrap = page.querySelector('#sedr-guest-password-wrap') as HTMLLabelElement;
  const passwordInput = page.querySelector('input[name="password"]') as HTMLInputElement;

  let entering = false;
  let gateResponseText = '';
  let gateInfo: SedrGateResponse | null = null;
  let errorKey: string | null = null;
  let statusKey: string | null = null;
  let pendingToken: string | null = null;
  let pendingFragment = guestFragment;
  let expiryMonitor: GuestAccessExpiryHandle | null = null;
  let expiryWarningEl: HTMLElement | null = null;

  const showExpiryWarning = (remainingMs: number) => {
    const minutes = Math.max(1, Math.ceil(remainingMs / 60_000));
    if (!expiryWarningEl) {
      expiryWarningEl = document.createElement('aside');
      expiryWarningEl.className = 'sedr-header-banner sedr-header-banner--requested';
      expiryWarningEl.id = 'sedr-guest-expiry-warning';
      expiryWarningEl.setAttribute('role', 'status');
      const text = document.createElement('div');
      text.className = 'sedr-header-banner__text';
      const label = document.createElement('strong');
      label.className = 'sedr-header-banner__label';
      label.id = 'sedr-guest-expiry-warning-text';
      text.append(label);
      expiryWarningEl.append(text);
    }
    const textEl = expiryWarningEl.querySelector('#sedr-guest-expiry-warning-text') as HTMLElement;
    textEl.textContent = t('sedr.guest.expiresSoon', { minutes });
    const bannerSlot = page.querySelector('#sedr-room-banner-slot') as HTMLElement | null;
    if (bannerSlot) {
      bannerSlot.prepend(expiryWarningEl);
    } else if (!expiryWarningEl.isConnected) {
      page.prepend(expiryWarningEl);
    }
  };

  const clearExpiryWarning = () => {
    expiryWarningEl?.remove();
  };

  const startExpiryMonitor = (expiresAt: string | null | undefined) => {
    expiryMonitor?.dispose();
    expiryMonitor = null;
    clearExpiryWarning();
    if (!expiresAt) return;
    expiryMonitor = startGuestAccessExpiryMonitor({
      expiresAt,
      refreshExpiry: async () => {
        const text = await sedrApi.guestGate(route.roomToken, route.guestInviteId);
        const gate = JSON.parse(text) as SedrGateResponse;
        gateInfo = gate;
        return {
          expired: gate.expired,
          revoked: gate.revoked,
          expiresAt: gate.expiresAt,
        };
      },
      onWarning: showExpiryWarning,
      onWarningClear: clearExpiryWarning,
      onExpiredLogout: async () => {
        await logoutGuest();
        if (!page.isConnected) return;
        expiryMonitor?.dispose();
        expiryMonitor = null;
        clearExpiryWarning();
        page.className = 'center-panel guest-panel';
        page.innerHTML = `
          <p class="muted" id="sedr-guest-status"></p>
        `;
        const status = page.querySelector('#sedr-guest-status') as HTMLParagraphElement;
        status.textContent = t('sedr.guest.expired');
      },
    });
  };

  const busyOptions = {
    titleKey: 'sedr.guest.entering',
    detailKey: 'sedr.guest.enteringDetail',
  };

  const guestErrorKey = (cause: unknown): string => {
    const code = cause instanceof ApiError
      ? cause.code
      : cause instanceof Error
        ? cause.message
        : '';
    switch (code) {
      case 'invalid_link':
        return 'sedr.guest.invalidLink';
      case 'access_denied':
        return 'sedr.guest.revoked';
      case 'incorrect_password':
        return 'info.guest.incorrectPassword';
      case 'incorrect_totp':
        return 'sedr.guest.incorrectTotp';
      case 'totp_pending_expired':
        return 'sedr.guest.totpPendingExpired';
      case 'nonce_expired':
        return 'info.guest.nonceExpired';
      case 'missing_fragment':
        return 'sedr.guest.missingFragment';
      case 'verify_locked_out':
        return 'errors.verifyLockedOut';
      default:
        return 'status.error';
    }
  };

  const hideGateForms = () => {
    form.hidden = true;
    enrollPanel.hidden = true;
    challengeForm.hidden = true;
  };

  const showEnroll = (verify: SedrVerifyResponse) => {
    hideGateForms();
    pendingToken = verify.pendingToken ?? null;
    secretInput.value = verify.totpSecret ?? '';
    qrHost.innerHTML = '';
    if (verify.otpauthUri) {
      qrHost.innerHTML = renderSVG(verify.otpauthUri, { ecc: 'M', pixelSize: 4 });
    }
    enrollPanel.hidden = false;
    const codeInput = enrollForm.querySelector('input[name="totpCode"]') as HTMLInputElement;
    codeInput.value = '';
    codeInput.focus();
  };

  const showChallenge = (verify: SedrVerifyResponse) => {
    hideGateForms();
    pendingToken = verify.pendingToken ?? null;
    challengeForm.hidden = false;
    const codeInput = challengeForm.querySelector('input[name="totpCode"]') as HTMLInputElement;
    codeInput.value = '';
    codeInput.focus();
  };

  const finishEntry = async () => {
    const fragment = pendingFragment ?? resolveSedrGuestFragment(route.roomToken);
    guestFragment = fragment;
    pendingFragment = fragment;
    if (!fragment?.ps) {
      showStatus('sedr.guest.missingFragment');
      return;
    }

    roomKey = await fetchAndDecryptSedrGuestRoomKey(
      route.roomToken,
      route.guestInviteId,
      fragment,
      lastGuestPassword,
    );

    await refreshRoom();
    verified = true;
    startExpiryMonitor(gateInfo?.expiresAt);
  };

  const handleVerifyResponse = async (verify: SedrVerifyResponse) => {
    if (verify.nextStep === 'enroll') {
      setCryptoBusy(gateSection, form, false, busyOptions);
      showEnroll(verify);
      return;
    }
    if (verify.nextStep === 'challenge') {
      setCryptoBusy(gateSection, form, false, busyOptions);
      showChallenge(verify);
      return;
    }
    await finishEntry();
  };

  const showError = (key: string) => {
    errorKey = key;
    statusKey = null;
    errorEl.textContent = t(key);
    errorEl.hidden = false;
    statusEl.hidden = true;
  };

  const showStatus = (key: string) => {
    statusKey = key;
    errorKey = null;
    statusEl.textContent = t(key);
    statusEl.hidden = false;
    errorEl.hidden = true;
    form.hidden = true;
  };

  const layoutEls = () => ({
    layout: page.querySelector('.sedr-room-layout') as HTMLElement | null,
    sidebar: page.querySelector('#sedr-sidebar') as HTMLElement | null,
    titleEl: page.querySelector('#sedr-room-title') as HTMLElement | null,
    orgEl: page.querySelector('#sedr-room-org') as HTMLElement | null,
    panelSlot: page.querySelector('#sedr-room-panel-slot') as HTMLElement | null,
    bannerSlot: page.querySelector('#sedr-room-banner-slot') as HTMLElement | null,
    actionsSlot: page.querySelector('#sedr-room-actions') as HTMLElement | null,
    tabContent: page.querySelector('#sedr-tab-content') as HTMLElement,
  });

  const getNavTabs = (): SedrChromeTab[] => [
    { id: 'dashboard', labelKey: 'sedr.nav.dashboard' },
    { id: 'compliance', labelKey: 'sedr.nav.compliance' },
    { id: 'evidence', labelKey: 'sedr.nav.evidence' },
    { id: 'audit', labelKey: 'sedr.nav.audit' },
  ];

  const selectNavTab = (tabId: string) => {
    activeTab = tabId as SedrGuestTab;
    renderSidebar();
    updateRoomHelpTip();
    void renderTab();
  };

  const renderSidebar = () => {
    const { layout, sidebar } = layoutEls();
    if (!layout || !sidebar) return;
    const sidebarCollapsed = isSidebarCollapsed();
    applySidebarCollapsed(layout, sidebar, sidebarCollapsed);
    if (sidebarCollapsed) return;

    const tabs = getNavTabs();
    sidebar.innerHTML = `
      <h2 class="sedr-sidebar__heading" data-i18n="sedr.nav.heading"></h2>
      <nav class="sedr-sidebar-nav" data-i18n-aria-label="sedr.nav.heading"></nav>
    `;
    const nav = sidebar.querySelector('.sedr-sidebar-nav') as HTMLElement;
    for (const tab of tabs) {
      const link = document.createElement('button');
      link.type = 'button';
      link.className = `sedr-sidebar-link${activeTab === tab.id ? ' sedr-sidebar-link--active' : ''}`;
      link.dataset.tab = tab.id;
      link.setAttribute('data-i18n', tab.labelKey);
      link.addEventListener('click', () => selectNavTab(tab.id));
      nav.append(link);
    }
    applyI18n(sidebar);
  };

  const updateRoomHelpTip = () => {
    const { titleEl, panelSlot, actionsSlot } = layoutEls();
    const topic = sedrTabHelpTopic(activeTab, { guest: true });
    page.setAttribute('data-help-context', topic);
    if (!titleEl || !ctx) return;
    const sidebarCollapsed = isSidebarCollapsed();
    titleEl.textContent = ctx.title;
    if (panelSlot) {
      panelSlot.replaceChildren(createActivePanelControl({
        activeTab,
        tabs: getNavTabs(),
        navHidden: sidebarCollapsed,
        onSelect: selectNavTab,
      }));
      applyI18n(panelSlot);
    }
    if (actionsSlot) {
      chromeActionsCleanup?.();
      const { fragment, cleanup } = renderSedrRoomChromeActions({
        fullscreenTarget: page,
        sidebarCollapsed,
        onToggleSidebar: () => {
          sidebarPreference = !sidebarPreference;
          writeSidebarCollapsed(sidebarPreference);
          renderSidebar();
          updateRoomHelpTip();
        },
        helpTopic: topic,
      });
      chromeActionsCleanup = cleanup;
      actionsSlot.replaceChildren(fragment);
      applyI18n(actionsSlot);
    }
  };

  const renderUpgradeBanner = (): HTMLElement | null => {
    const email = ctx?.guestEmail ?? gateInfo?.guestEmail ?? null;
    const banner = document.createElement('div');
    banner.className = 'warning-panel sedr-guest-upgrade-banner';
    if (!email) {
      banner.innerHTML = `
        <span class="warning-panel__icon"></span>
        <div>
          <p data-i18n="sedr.guest.upgradeNeedsEmail"></p>
        </div>
      `;
      applyI18n(banner);
      return banner;
    }

    const canMigrate = Boolean(gateInfo?.totpEnrolled || ctx?.requireGuestTotp);
    banner.innerHTML = `
      <span class="warning-panel__icon"></span>
      <div class="sedr-guest-upgrade-banner__body">
        <p data-i18n="sedr.guest.upgradeHint"></p>
        <p class="muted">${t('sedr.guest.upgradeEmailLabel', { email })}</p>
        ${canMigrate && gateInfo?.totpEnrolled
          ? `<label class="checkbox-row">
              <input type="checkbox" id="sedr-guest-migrate-totp" checked />
              <span data-i18n="sedr.guest.upgradeMigrateTotp"></span>
            </label>`
          : ''}
        <button type="button" class="button primary" id="sedr-guest-upgrade-btn" data-i18n="sedr.guest.upgradeButton"></button>
        <p class="error-text" id="sedr-guest-upgrade-error" hidden></p>
      </div>
    `;
    applyI18n(banner);
    banner.querySelector('#sedr-guest-upgrade-btn')?.addEventListener('click', () => {
      void startUpgradeAccount();
    });
    return banner;
  };

  const startUpgradeAccount = async () => {
    const errorEl = page.querySelector('#sedr-guest-upgrade-error') as HTMLElement | null;
    const btn = page.querySelector('#sedr-guest-upgrade-btn') as HTMLButtonElement | null;
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    const fragment = guestFragment ?? resolveSedrGuestFragment(route.roomToken);
    if (!fragment?.ps) {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = t('sedr.guest.missingFragment');
      }
      return;
    }
    if (btn) btn.disabled = true;
    try {
      const wrapKey = await resolveSedrGuestWrapKey(fragment, lastGuestPassword);
      if (!wrapKey) {
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = t('sedr.guest.upgradeWrapKeyMissing');
        }
        return;
      }
      const migrateInput = page.querySelector('#sedr-guest-migrate-totp') as HTMLInputElement | null;
      const migrateTotp = migrateInput ? migrateInput.checked : true;
      const started = await sedrApi.guestUpgradeStart(route.roomToken, route.guestInviteId);
      upsertPendingGuestInviteSeed({
        inviteId: started.inviteId,
        roomId: started.roomId,
        roomServerToken: started.roomServerToken,
        envelopeBlobId: started.envelopeBlobId,
        guestWrapKey: encodeBase64Url(wrapKey),
        displayName: started.displayName,
        organization: started.organization,
        email: started.email,
        guestRole: started.guestRole,
        allowCommenting: started.allowCommenting,
        migrateTotp: started.canMigrateTotp ? migrateTotp : false,
      });
      window.location.assign('/guest/?upgrade=1');
    } catch (cause) {
      const code = (cause as { code?: string } | null)?.code;
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = code === 'email_required_for_upgrade'
          ? t('sedr.guest.upgradeNeedsEmail')
          : t('status.error');
      }
      console.error(cause);
      if (btn) btn.disabled = false;
    }
  };

  const renderHeader = () => {
    const { titleEl, orgEl, bannerSlot } = layoutEls();
    if (!titleEl || !ctx) return;
    updateRoomHelpTip();
    if (orgEl) {
      orgEl.textContent = ctx.clientOrganization ? `· ${ctx.clientOrganization}` : '';
      orgEl.hidden = !ctx.clientOrganization;
    }
    if (bannerSlot) {
      const keepExpiry =
        expiryWarningEl && expiryWarningEl.parentElement === bannerSlot ? expiryWarningEl : null;
      bannerSlot.replaceChildren();
      if (keepExpiry) bannerSlot.prepend(keepExpiry);
      const banner = renderRoomStatusBanner(ctx);
      if (banner) bannerSlot.append(banner);
      const upgradeBanner = renderUpgradeBanner();
      if (upgradeBanner) bannerSlot.append(upgradeBanner);
    }
  };

  const renderCompliance = () => {
    const { tabContent } = layoutEls();
    if (!ctx || !tabContent) return;
    if (selectedRequirementId) {
      const selected = requirements.find((r) => r.id === selectedRequirementId);
      if (selected && isSedrLifecycleClosed(selected.status)) {
        complianceHideObsolete = false;
      }
    }
    if (compliancePanel?.element.isConnected) {
      compliancePanel.update({
        requirements,
        phases: phasesList.phases,
        files,
      });
      return;
    }
    compliancePanel = renderSedrCompliancePanel({
      roomId: ctx.roomId,
      roomTitle: ctx.title,
      clientOrganization: ctx.clientOrganization,
      requirements,
      phases: phasesList.phases,
      files,
      canWrite: false,
      canAdmin: false,
      showExport: true,
      showCreate: false,
      showImport: false,
      initial: {
        statusFilter: complianceStatusFilter,
        categoryFilter: complianceCategoryFilter,
        phaseId: compliancePhaseId,
        search: complianceSearch,
        commentSearch: complianceCommentSearch,
        hideObsolete: complianceHideObsolete,
        obsoleteSubtypes: complianceObsoleteSubtypes,
        ownerFilter: complianceOwnerFilter,
        renewalFilter: complianceRenewalFilter,
        lifecycleRequestFilter: complianceLifecycleRequestFilter,
        evidenceFilter: complianceEvidenceFilter,
        selectedRequirementIds: [...selectedRequirementIds],
      },
      onStateChange: (state) => {
        complianceStatusFilter = state.statusFilter;
        complianceCategoryFilter = state.categoryFilter;
        compliancePhaseId = state.phaseId;
        complianceSearch = state.search;
        complianceCommentSearch = state.commentSearch;
        complianceHideObsolete = state.hideObsolete;
        complianceObsoleteSubtypes = state.obsoleteSubtypes;
        complianceOwnerFilter = state.ownerFilter;
        complianceRenewalFilter = state.renewalFilter;
        complianceLifecycleRequestFilter = state.lifecycleRequestFilter;
        complianceEvidenceFilter = state.evidenceFilter;
        selectedRequirementIds = [...state.selectedRequirementIds];
        selectedRequirementId = state.selectedRequirementIds.length === 1
          ? state.selectedRequirementIds[0]!
          : null;
      },
      onOpenFile: (file, context) => openFile(file, undefined, context),
      onDownloadFile: (file) => downloadFile(file),
      onRequirementsChanged: async () => {
        requirements = await sedrApi.guestListRequirements(route.roomToken, route.guestInviteId);
        phasesList = await sedrApi.guestListPhases(route.roomToken, route.guestInviteId);
        files = await sedrApi.guestListFiles(route.roomToken, route.guestInviteId);
        renderCompliance();
      },
      onOpenRequirement: (requirementId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId,
          phaseId: null,
        });
      },
      guest: {
        roomToken: route.roomToken,
        guestInviteId: route.guestInviteId,
        canComment: Boolean(ctx.allowCommenting),
        canApprove: (ctx.guestRole ?? 'GuestReader') === 'GuestApprover',
      },
    });
    tabContent.replaceChildren(compliancePanel.element);
  };

  const openEvidenceBytes = async (file: SedrFile): Promise<Blob> => {
    const key = sedrEvidenceNeedsRoomKey(file) ? roomKey : null;
    if (sedrEvidenceNeedsRoomKey(file) && !key) throw new Error('room_key_missing');
    const bytes = await downloadAndDecryptSedrEvidence(file, key);
    const copy = new Uint8Array(bytes);
    const buffer = copy.buffer.slice(copy.byteOffset, copy.byteOffset + copy.byteLength) as ArrayBuffer;
    return new Blob([buffer], { type: file.contentType || 'application/octet-stream' });
  };

  const openFile = async (
    file: SedrFile,
    onError?: (message: string) => void,
    context?: { gallery: SedrFile[] },
  ) => {
    if (!file.blobId || !ctx) return;
    try {
      await sedrApi.guestRecordFileOpened(route.roomToken, route.guestInviteId, file.id);
      const blob = await openEvidenceBytes(file);
      const gallery = context?.gallery?.filter((f) => Boolean(f.blobId));
      if (gallery && gallery.length > 1) {
        const initialIndex = Math.max(0, gallery.findIndex((f) => f.id === file.id));
        await showFilePreview(file.name, blob, file.contentType, {
          items: gallery.map((f) => ({
            id: f.id,
            fileName: f.name,
            contentType: f.contentType,
          })),
          initialIndex,
          loadItem: async (item) => {
            const target = gallery.find((f) => f.id === item.id);
            if (!target?.blobId) throw new Error('missing_gallery_file');
            await sedrApi.guestRecordFileOpened(route.roomToken, route.guestInviteId, target.id);
            return openEvidenceBytes(target);
          },
        });
        return;
      }
      await showFilePreview(file.name, blob, file.contentType);
    } catch (cause) {
      console.error('SEDR guest preview failed', cause);
      onError?.(t('status.error'));
    }
  };

  const downloadFile = async (file: SedrFile, onError?: (message: string) => void) => {
    if (!file.blobId) return;
    try {
      const blob = await openEvidenceBytes(file);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.append(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (cause) {
      console.error('SEDR guest download failed', cause);
      onError?.(t('status.error'));
    }
  };

  const renderEvidence = () => {
    const { tabContent } = layoutEls();
    const panel = document.createElement('div');
    panel.className = 'sedr-tab-panel sedr-tab-panel--evidence';

    const showEvidenceError = (message: string) => {
      const errorEl = panel.querySelector('#sedr-evidence-error') as HTMLParagraphElement | null;
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = message;
      }
    };

    renderSedrEvidenceExplorer(panel, {
      files,
      readOnly: true,
      formatSize: formatFileSize,
      initialParentId: evidenceParentId,
      initialSearch: evidenceSearch,
      isBusy: () => evidenceBusy,
      onFolderChange: (parentId) => {
        evidenceParentId = parentId;
      },
      onSearchChange: (query) => {
        evidenceSearch = query;
      },
      onOpenFile: (file) => openFile(file, showEvidenceError),
      onDownloadFile: (file) => downloadFile(file, showEvidenceError),
      onNewFolder: async () => {},
      onUploadFiles: () => {},
    });

    tabContent.replaceChildren(panel);
    applyI18n(panel);
  };

  const renderAudit = async () => {
    const { tabContent } = layoutEls();
    const panel = document.createElement('div');
    panel.className = 'sedr-tab-panel sedr-audit-panel';
    const [events, categories, reqs, phases, roomFiles] = await Promise.all([
      sedrApi.guestListAuditEvents(route.roomToken, route.guestInviteId),
      sedrApi.guestListCategories(route.roomToken, route.guestInviteId).catch(() => []),
      requirements.length > 0
        ? Promise.resolve(requirements)
        : sedrApi.guestListRequirements(route.roomToken, route.guestInviteId),
      phasesList.phases.length > 0
        ? Promise.resolve(phasesList)
        : sedrApi.guestListPhases(route.roomToken, route.guestInviteId),
      files.length > 0
        ? Promise.resolve(files)
        : sedrApi.guestListFiles(route.roomToken, route.guestInviteId),
    ]);
    requirements = reqs;
    phasesList = phases;
    files = roomFiles;
    seedSedrCategoriesCache(ctx!.roomId, categories);

    const lookup = buildSedrAuditLookup({
      requirements,
      phases: phasesList.phases,
      files,
      categories,
    });

    mountSedrAuditExpandableTable(panel, events, {
      lookup,
      onOpenRequirement: (requirementId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId,
          phaseId: null,
        });
      },
      onOpenPhase: (phaseId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId: null,
          phaseId,
        });
      },
      onOpenFile: (fileId) => {
        const file = files.find((f) => f.id === fileId);
        evidenceParentId = file?.parentId ?? null;
        openEvidence();
      },
    });
    tabContent.replaceChildren(panel);
  };

  const openCompliance = (opts?: {
    statusFilter?: SedrStatusFilter;
    categoryFilter?: SedrCategoryFilter;
    requirementId?: string | null;
    phaseId?: CompliancePhaseId;
  }) => {
    if (opts?.statusFilter !== undefined) complianceStatusFilter = opts.statusFilter;
    if (opts?.categoryFilter !== undefined) complianceCategoryFilter = opts.categoryFilter;
    if (opts?.requirementId !== undefined) {
      selectedRequirementId = opts.requirementId;
      selectedRequirementIds = selectedRequirementId ? [selectedRequirementId] : [];
      if (selectedRequirementId) {
        const selected = requirements.find((r) => r.id === selectedRequirementId);
        if (selected && isSedrLifecycleClosed(selected.status)) {
          complianceHideObsolete = false;
        }
      }
    }
    if (opts?.phaseId !== undefined) compliancePhaseId = opts.phaseId;
    compliancePanel = null;
    activeTab = 'compliance';
    renderSidebar();
    updateRoomHelpTip();
    void renderTab();
  };

  const openAudit = () => {
    activeTab = 'audit';
    renderSidebar();
    updateRoomHelpTip();
    void renderTab();
  };

  const openEvidence = () => {
    activeTab = 'evidence';
    renderSidebar();
    updateRoomHelpTip();
    void renderTab();
  };

  const handleMetricSelect = (metric: SedrMetricKind) => {
    switch (metric) {
      case 'total':
        openCompliance({ statusFilter: [], categoryFilter: 'all', requirementId: null, phaseId: null });
        break;
      case 'evidence':
        openEvidence();
        break;
      case 'pendingApproval':
        openCompliance({ statusFilter: ['UnderReview'], categoryFilter: 'all', requirementId: null, phaseId: null });
        break;
      case 'approved':
        openCompliance({ statusFilter: ['ApprovedDischarged'], categoryFilter: 'all', requirementId: null, phaseId: null });
        break;
    }
  };

  const renderDashboard = () => {
    const { tabContent } = layoutEls();
    if (!ctx) return;
    const roomCtx = ctx;
    tabContent.replaceChildren(renderSedrDashboard({
      metrics: roomCtx.metrics,
      requirements,
      phasesList,
      auditEvents,
      canApprove: Boolean(roomCtx.canApprove),
      canManagePhases: false,
      onSelectMetric: handleMetricSelect,
      onOpenRequirement: (requirementId) => {
        openCompliance({ statusFilter: [], categoryFilter: 'all', requirementId, phaseId: null });
      },
      onViewAttentionAll: () => {
        openCompliance({
          statusFilter: roomCtx.canApprove ? ['UnderReview'] : ['PendingUpload', 'Denied'],
          categoryFilter: 'all',
          requirementId: null,
          phaseId: null,
        });
      },
      onSelectPhase: (phaseId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId: null,
          phaseId: phaseId === null ? 'unassigned' : phaseId,
        });
      },
      onViewAudit: openAudit,
    }));
  };

  const renderTab = async () => {
    switch (activeTab) {
      case 'dashboard':
        requirements = await sedrApi.guestListRequirements(route.roomToken, route.guestInviteId);
        phasesList = await sedrApi.guestListPhases(route.roomToken, route.guestInviteId);
        auditEvents = await sedrApi.guestListAuditEvents(route.roomToken, route.guestInviteId);
        renderDashboard();
        break;
      case 'compliance':
        requirements = await sedrApi.guestListRequirements(route.roomToken, route.guestInviteId);
        phasesList = await sedrApi.guestListPhases(route.roomToken, route.guestInviteId);
        files = await sedrApi.guestListFiles(route.roomToken, route.guestInviteId);
        {
          const categories = await sedrApi
            .guestListCategories(route.roomToken, route.guestInviteId)
            .catch(() => []);
          if (ctx) seedSedrCategoriesCache(ctx.roomId, categories);
        }
        renderCompliance();
        break;
      case 'evidence':
        files = await sedrApi.guestListFiles(route.roomToken, route.guestInviteId);
        renderEvidence();
        break;
      case 'audit':
        await renderAudit();
        break;
    }
  };

  const mountRoomLayout = () => {
    page.className = 'module-page module-page--sedr sedr-room-page';
    page.innerHTML = `
      <div class="sedr-room-layout">
        <aside class="sedr-sidebar" id="sedr-sidebar"></aside>
        <div class="sedr-room-main files-browse-main">
          <div class="files-browse-chrome sedr-room-chrome">
            <div class="files-browse-chrome__identity">
              <h2 class="files-browse-chrome__title" id="sedr-room-title"></h2>
              <span class="sedr-room-chrome__org" id="sedr-room-org"></span>
              <span class="sedr-room-chrome__panel-slot" id="sedr-room-panel-slot"></span>
              <span id="sedr-room-banner-slot"></span>
            </div>
            <div class="files-browse-chrome__actions" id="sedr-room-actions"></div>
          </div>
          <div id="sedr-tab-content" class="sedr-room-main__body"></div>
        </div>
      </div>
    `;
  };

  const refreshRoom = async () => {
    ctx = await sedrApi.guestRoomContext(route.roomToken, route.guestInviteId);
    setGuestShellName(ctx.guestDisplayName, ctx.guestOrganization);
    if (!page.querySelector('.sedr-room-layout')) {
      mountRoomLayout();
    }
    renderSidebar();
    renderHeader();
    await renderTab();
  };

  const completeEntry = async () => {
    if (!gateInfo || entering) {
      return;
    }
    entering = true;
    errorEl.hidden = true;
    setCryptoBusy(gateSection, form, true, busyOptions);
    try {
      if (gateInfo.revoked || gateInfo.expired) {
        showStatus(gateInfo.revoked ? 'sedr.guest.revoked' : 'sedr.guest.expired');
        return;
      }

      const fragment = guestFragment ?? resolveSedrGuestFragment(route.roomToken);
      guestFragment = fragment;
      pendingFragment = fragment;
      if (!fragment?.ps) {
        showStatus('sedr.guest.missingFragment');
        return;
      }

      const { secretKey } = deriveProofKeys(decodeBase64Url(fragment.ps));
      const signature = await signGateChallenge(secretKey, gateInfo.nonce, gateResponseText);
      lastGuestPassword = passwordInput.value || undefined;
      const verify = await sedrApi.guestVerify(route.roomToken, route.guestInviteId, {
        signature,
        password: lastGuestPassword,
      });

      await handleVerifyResponse(verify);
    } catch (cause) {
      showError(guestErrorKey(cause));
    } finally {
      if (!verified && enrollPanel.hidden && challengeForm.hidden) {
        setCryptoBusy(gateSection, form, false, busyOptions);
      }
      entering = false;
    }
  };

  const submitTotp = async (code: string, mode: 'enroll' | 'challenge') => {
    if (!pendingToken || entering) {
      return;
    }
    entering = true;
    errorEl.hidden = true;
    const activeForm = mode === 'enroll' ? enrollForm : challengeForm;
    setCryptoBusy(gateSection, activeForm, true, busyOptions);
    try {
      if (mode === 'enroll') {
        await sedrApi.guestTotpConfirm(route.roomToken, route.guestInviteId, {
          pendingToken,
          code,
        });
      } else {
        await sedrApi.guestTotpVerify(route.roomToken, route.guestInviteId, {
          pendingToken,
          code,
        });
      }
      hideGateForms();
      await finishEntry();
    } catch (cause) {
      showError(guestErrorKey(cause));
    } finally {
      if (!verified) {
        setCryptoBusy(gateSection, activeForm, false, busyOptions);
      }
      entering = false;
    }
  };

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (entering) return;
    void completeEntry();
  });

  enrollForm.addEventListener('submit', (e) => {
    e.preventDefault();
    if (entering) return;
    const code = String(new FormData(enrollForm).get('totpCode') ?? '');
    void submitTotp(code, 'enroll');
  });

  challengeForm.addEventListener('submit', (e) => {
    e.preventDefault();
    if (entering) return;
    const code = String(new FormData(challengeForm).get('totpCode') ?? '');
    void submitTotp(code, 'challenge');
  });

  bindLocaleRefresh(() => {
    if (verified) {
      void refreshRoom();
      return;
    }
    applyI18n(page);
    if (errorKey && errorEl.isConnected && !errorEl.hidden) {
      errorEl.textContent = t(errorKey);
    }
    if (statusKey && statusEl.isConnected && !statusEl.hidden) {
      statusEl.textContent = t(statusKey);
    }
  });

  void (async () => {
    setCryptoBusy(gateSection, form, true, busyOptions);
    try {
      // Resume an existing guest session (e.g. after HMR/reload) without re-challenging TOTP.
      try {
        await sedrApi.guestRoomContext(route.roomToken, route.guestInviteId);
        const fragment = guestFragment ?? resolveSedrGuestFragment(route.roomToken);
        guestFragment = fragment;
        pendingFragment = fragment;
        if (!fragment?.ps) {
          setCryptoBusy(gateSection, form, false, busyOptions);
          showStatus('sedr.guest.missingFragment');
          return;
        }
        // Refresh gate so we know access expiry for the in-session monitor.
        try {
          gateResponseText = await sedrApi.guestGate(route.roomToken, route.guestInviteId);
          gateInfo = JSON.parse(gateResponseText) as SedrGateResponse;
        } catch {
          // Gate refresh is best-effort when resuming; room context already succeeded.
        }
        await finishEntry();
        return;
      } catch (cause) {
        if (!(cause instanceof ApiError) || cause.status !== 401) {
          throw cause;
        }
      }

      gateResponseText = await sedrApi.guestGate(route.roomToken, route.guestInviteId);
      gateInfo = JSON.parse(gateResponseText) as SedrGateResponse;

      if (gateInfo.revoked || gateInfo.expired) {
        setCryptoBusy(gateSection, form, false, busyOptions);
        showStatus(gateInfo.revoked ? 'sedr.guest.revoked' : 'sedr.guest.expired');
        return;
      }

      if (gateInfo.passwordRequired) {
        setCryptoBusy(gateSection, form, false, busyOptions);
        passwordWrap.hidden = false;
        passwordInput.required = true;
        form.hidden = false;
        passwordInput.focus();
        return;
      }

      await completeEntry();
    } catch (cause) {
      setCryptoBusy(gateSection, form, false, busyOptions);
      showStatus(guestErrorKey(cause));
    }
  })();

  const onSidebarMqChange = () => {
    if (!verified) return;
    renderSidebar();
    updateRoomHelpTip();
  };
  sidebarMq.addEventListener('change', onSidebarMqChange);

  const observer = new MutationObserver(() => {
    if (!page.isConnected) {
      expiryMonitor?.dispose();
      expiryMonitor = null;
      clearExpiryWarning();
      sidebarMq.removeEventListener('change', onSidebarMqChange);
      chromeActionsCleanup?.();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return page;
}
